import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Download, FileSpreadsheet } from "lucide-react";
import { useSnackbar } from "../context/SnackbarContext";
import { downloadOrganizerWorkbook } from "../utils/imports/downloadOrganizerWorkbook";

interface SheetRow {
  name: string;
  ready: boolean;
  phase: "Phase 0" | "Phase 1" | "Phase 2" | "Reference";
  description: string;
}

const SHEETS: SheetRow[] = [
  {
    name: "Users",
    ready: true,
    phase: "Phase 0",
    description:
      "People in the system, with full profile data. Uploads via the Users page. Skip this if everyone you're inviting is already on the platform from a prior event.",
  },
  {
    name: "Roster",
    ready: true,
    phase: "Phase 1",
    description:
      "Per-event list of attendees — one email per row. Uploads via the Attendees page. Each email is matched against the global Users + Contacts list.",
  },
  {
    name: "Locations",
    ready: true,
    phase: "Phase 1",
    description:
      "Places: venue, restaurants, hotels, meeting rooms. Sessions and Travel reference these by label.",
  },
  {
    name: "Events",
    ready: false,
    phase: "Reference",
    description:
      "Reference only — events are created one at a time in the admin's Create Event form.",
  },
  {
    name: "Groups",
    ready: true,
    phase: "Phase 2",
    description:
      "Custom groups (Shuttle A, Dinner Group, etc.). The six system groups (All Members / All Invitees / All Companions / All Internal Team / All Organizers / All Admins) are auto-created when you make an event — don't list them here.",
  },
  {
    name: "Sessions",
    ready: true,
    phase: "Phase 2",
    description:
      "Agenda items. References Locations by label, Groups by name, and speaker Users by email.",
  },
  {
    name: "Flights",
    ready: true,
    phase: "Phase 2",
    description: "Travel — flights.",
  },
  {
    name: "Lodging",
    ready: true,
    phase: "Phase 2",
    description: "Travel — hotels and lodging.",
  },
  {
    name: "Transport",
    ready: true,
    phase: "Phase 2",
    description:
      "Travel — shuttles, rideshares, taxis, trains, ferries, walks. Optional sessionLink references a Session by title, so import Travel after Sessions exist.",
  },
  {
    name: "Other Travel",
    ready: true,
    phase: "Phase 2",
    description: "Travel — anything that doesn't fit the other three.",
  },
];

const CONVENTIONS: { title: string; body: string }[] = [
  {
    title: "Dates and times",
    body: "Dates use YYYY-MM-DD. Date+time fields use YYYY-MM-DD HH:MM in the relevant local time.",
  },
  {
    title: "Comma-separated lists",
    body: "Where a column accepts multiple values (audienceGroups, memberRoles, etc.), separate them with commas. Quote any value that contains a comma.",
  },
  {
    title: "Boolean fields",
    body: "Use TRUE or FALSE.",
  },
  {
    title: "Referencing groups",
    body: "By group NAME (not ID). System groups are named “All Members”, “All Invitees”, “All Companions”, “All Internal Team”, “All Organizers”, “All Admins”. Custom groups are whatever you named them in the Groups sheet.",
  },
  {
    title: "Referencing people",
    body: "By email address. The person must be on this event's roster (the Roster sheet, or invited individually via the Attendees page). Pending contacts who haven't signed in yet still resolve correctly — their personal audience is created when they're added to the event.",
  },
  {
    title: "Referencing locations",
    body: "By Location LABEL (not ID).",
  },
  {
    title: "Phase 0 / Phase 1 / Phase 2",
    body: "Phase 0 (skip if not needed): add brand-new people to the platform via the Users sheet. Phase 1: per-event setup — fill the Roster (emails) and Locations sheets, then create the event in the admin. Phase 2: per-event content — Groups, then Sessions, then all Travel last (Transport with sessionLink references Sessions, so do this after Sessions exist).",
  },
];

/**
 * Standalone landing page for the organizer planning workbook download.
 * Conventions and per-sheet guidance live here rather than as a README
 * sheet inside the .xlsx — Excel renders single-column text rows as a
 * grid, which looks broken next to the actual data sheets.
 */
export function PlanningWorkbookPage() {
  const { showError } = useSnackbar();
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadOrganizerWorkbook();
    } catch (err) {
      showError(
        err instanceof Error
          ? `Workbook download failed: ${err.message}`
          : "Workbook download failed.",
      );
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: { xs: 4, md: 8 },
        gap: 4,
      }}
    >
      <Card
        variant="outlined"
        sx={{ maxWidth: 560, width: "100%" }}
        data-testid="planning-workbook-card"
      >
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <FileSpreadsheet size={28} />
            </Box>

            <Stack spacing={1.5}>
              <Typography variant="h4">Planning Workbook</Typography>
              <Typography variant="body1" color="text.secondary">
                A multi-sheet Excel workbook for planning an event before
                you start filling things into the admin. One sheet per
                kind of thing you'll create — Users, Roster, Locations,
                Groups, Sessions, plus the four travel kinds.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                When you're ready to upload, drop the same workbook into
                any Import button. Each dialog reads only its own sheet
                by name and skips the example row, so you can use one
                file end-to-end. No transcription.
              </Typography>
            </Stack>

            <Button
              variant="contained"
              size="large"
              startIcon={<Download size={18} />}
              onClick={handleDownload}
              disabled={downloading}
              data-testid="planning-workbook-download"
              sx={{ alignSelf: "stretch" }}
            >
              {downloading ? "Generating…" : "Download workbook (.xlsx)"}
            </Button>

            <Typography variant="body2" color="text.secondary">
              Tip: save a copy per event for a portable record and a head
              start on the next one.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ maxWidth: 720, width: "100%" }}>
        <Typography variant="h6" gutterBottom>
          What's in the workbook
        </Typography>
        <Stack spacing={3}>
          {(["Phase 0", "Phase 1", "Phase 2", "Reference"] as const).map(
            (phase) => {
              const sheetsInPhase = SHEETS.filter((s) => s.phase === phase);
              if (sheetsInPhase.length === 0) return null;
              return (
                <Box key={phase}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, letterSpacing: 0.6 }}
                  >
                    {phase === "Phase 0"
                      ? "Phase 0 — Global setup"
                      : phase === "Phase 1"
                        ? "Phase 1 — Per-event setup"
                        : phase === "Phase 2"
                          ? "Phase 2 — Per-event content"
                          : "Reference"}
                  </Typography>
                  <Stack spacing={1.5}>
                    {sheetsInPhase.map((sheet) => (
                      <Box
                        key={sheet.name}
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            flexShrink: 0,
                            mt: 0.75,
                            bgcolor: sheet.ready
                              ? "success.main"
                              : "grey.400",
                          }}
                          aria-hidden
                        />
                        <Box>
                          <Typography
                            component="span"
                            sx={{ fontWeight: 600 }}
                          >
                            {sheet.name}
                          </Typography>{" "}
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                          >
                            {sheet.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              );
            },
          )}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2 }}
        >
          Green dot = upload-ready (matches an Import button in the
          admin). Gray dot = reference only.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 720, width: "100%" }}>
        <Typography variant="h6" gutterBottom>
          Conventions
        </Typography>
        <Stack spacing={2}>
          {CONVENTIONS.map((c) => (
            <Box key={c.title}>
              <Typography variant="subtitle2">{c.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {c.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
